/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'src/redux/store';
import { dispatch } from 'src/redux/store';
import { useToggle, useRecordAttendToggle, useUnRecordAttendToggle, useConsultationAttendToggle, useArchivedToggle } from 'src/hooks';
import {
    OpenAttendedPopup,
    OpenUnAttendedPopup,
    OpenConsultationPopup,
    ContactLeadPopupRouter,
} from 'src/components/fullScreenPopUps';
import { ArchivedLeadPopUp } from 'src/components/dialogPopUp';
import { setCurrentTreatmentDataGlobal } from 'src/redux/slices/multiTreatment';
import {
    followUpSlice,
    getAppointment,
    clearLead,
    updateSteps,
    updateFollowUpLead,
    updateCompleteStatus,
} from 'src/redux/slices/lead';
import { beginArchiveLead } from 'src/redux/slices/lead/coreThunks';
import { getActivities } from 'src/redux/slices/activity';
import { setLoader } from 'src/redux/slices/loader';
import { localStorageRemoveItem } from 'src/utils/storage-available';
import { useSnackbar } from 'notistack';
import { lockBodyScroll } from 'src/utils/bodyScrollLock';
import { CreateLeadHeaderData } from 'src/constants/LeadDetailConst';
import { getTaskSummary, completeTask } from 'src/redux/slices/taskSlice';

// -----------------------------------------------
// useLegacyFollowUpFlow
//
// Encapsulates all V1 follow-up dialog state + handlers extracted from TaskDashboard.
// Returns imperative triggers + a `dialogs` fragment that must be rendered by the consumer.
//
// Usage:
//   const { triggerFollowUp, triggerContact, triggerAttendedPopup,
//           triggerAttendClick, triggerAttendFailedClick, dialogs } =
//     useLegacyFollowUpFlow({ practiceId });
//
//   // In JSX: {dialogs}
// -----------------------------------------------

// How long a contact popup may take to appear before it is given up on.
export const POPUP_WATCHDOG_MS = 6000;

export default function useLegacyFollowUpFlow({ practiceId, onClose: onCloseCallback, v2TaskId, completionOwnedExternally = false } = {}) {
    const dpatch = useDispatch();
    const navigate = useNavigate();

    const reminderDataId = useRef();
    const openContactWhatsappRef = useRef({ open: false }); // eslint-disable-line no-unused-vars
    const [Lead, setLead] = useState(0); // eslint-disable-line no-unused-vars

    const [rowLead, setRowLead] = useState({
        lead_id: '',
        treatment_id: '',
        price: '',
        first_name: '',
        last_name: '',
        status: '',
        treatment_name: '',
        created_at_human: '',
        lead_treatments__lead_contacted_counts__count: 0,
    });

    // The contact popup locks page scroll ~800 ms after it is asked for. Whatever ends the flow
    // (close, cancel, unmount, a second trigger) must clear that timer and take the lock off, or
    // the page behind stays frozen (CU-869fnzwvt R6).
    // Scroll is locked only once the popup is really on screen (the router's onReady), and a watchdog
    // closes a popup that never appears, with a message, instead of leaving a blank frozen page.
    const { enqueueSnackbar } = useSnackbar();
    const scrollTimer = useRef(null);
    const watchdog = useRef(null);
    const releaseScroll = useRef(null);
    const popupSeen = useRef(false);
    const endScrollLock = () => {
        if (scrollTimer.current) { clearTimeout(scrollTimer.current); scrollTimer.current = null; }
        if (watchdog.current) { clearTimeout(watchdog.current); watchdog.current = null; }
        popupSeen.current = false;
        if (releaseScroll.current) { releaseScroll.current(); releaseScroll.current = null; }
    };
    const onContactReady = () => {
        popupSeen.current = true;
        if (watchdog.current) { clearTimeout(watchdog.current); watchdog.current = null; }
        if (!releaseScroll.current) releaseScroll.current = lockBodyScroll();
    };
    useEffect(() => endScrollLock, []);

    // Archive popup receives legacySidebarData shape; for V2 callers we build it on demand
    const [archiveLeadData, setArchiveLeadData] = useState(null);

    const { toggle: openContacts, onOpen: onOpenContacts, onClose: onCloseContacts } = useToggle();
    const { toggle: openRecordAttend, onOpen: onOpenRecordAttend, onClose: onCloseRecordAttend } = useRecordAttendToggle();
    const { toggle: openUnRecordAttend, onOpen: onOpenUnRecordAttend, onClose: onCloseUnRecordAttend } = useUnRecordAttendToggle();
    const { toggle: openConsultationAttend, onOpen: onOpenConsultationAttend, onClose: onCloseConsultationAttend } = useConsultationAttendToggle();
    const { toggle: openArchiveLead, onOpen: onOpenArchiveLead, onClose: onCloseArchiveLead } = useArchivedToggle();

    // Normalise a reminder-shaped object from V1 or V2 cell data into rowLead
    const buildRowLeadFromData = (data) => {
        setRowLead({
            // Resolve the lead id from any shape — V1 reminder (lead_treatment.lead),
            // a V2 task object (lead.id / lead_treatment.lead), or a flat row (lead_id).
            // Opening from the tasks dashboard passes a task whose lead lives on
            // `data.lead`/`data.lead_treatment.lead`, NOT a flat lead_id — without these
            // fallbacks lead_id was '' and the Contact popup opened blank (no checklist).
            lead_id: data?.lead_treatment?.lead || data?.lead?.id || data?.lead_id || (typeof data?.lead === 'number' ? data.lead : '') || '',
            treatment_id: data?.lead_treatment?.id || '',
            price: data?.lead_treatment?.price || '',
            first_name: data?.lead?.first_name || '',
            last_name: data?.lead?.last_name || '',
            status: data?.lead_treatment?.status || '',
            treatment_name: data?.lead_treatment?.treatment || '',
            created_at_human: data?.lead?.created_at_human,
            lead_treatments__lead_contacted_counts__count:
                data?.lead_treatment?.lead_contacted_counts?.find(
                    (item) => item.status === data?.lead_treatment?.status
                )?.count || 0,
            clinCheckData: data?.lead_treatment?.leadtreatment_clincheck,
            leadTreatmentSpecification: data?.lead_treatment?.leadtreatment_specification,
            treatmentData: data,
        });
    };

    // ---- Close handlers ----

    // ── V2 task completion gate ──────────────────────────────────────────────
    //
    // INVARIANT: POST /tasks/{uuid}/complete/ must fire ONLY on a genuine
    // completing outcome, never on Cancel, backdrop-click, or Escape.
    //
    // Signal: AppointmentPage.onSubmit dispatches updateCompleteStatus(true)
    // (state.lead.markAsComplete) immediately before the popup calls onClose()
    // on the genuine "finish" path. On Cancel/backdrop/Escape the popup calls
    // handleClose() → dispatch(updateCompleteStatus(false)) → onClose(), so
    // markAsComplete is false when our handler runs.
    //
    // We read state.lead.markAsComplete at close time:
    //   • genuine outcome  → markAsComplete === true  → complete task + reset
    //   • cancel / Escape  → markAsComplete === false → skip completion
    //
    // UUID guard: composite grid-cell ids like `${leadId}-${idx}` from the
    // V2-flat Treatment Started table must never reach /tasks/{id}/complete/.
    // ────────────────────────────────────────────────────────────────────────
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    // Read live from Redux — set true by popup on genuine save, false on cancel
    const markAsComplete = useSelector((s) => s.lead?.markAsComplete);

    const completeV2TaskIfGenuineOutcome = () => {
        if (completionOwnedExternally) return;
        if (!v2TaskId || !practiceId) return;
        if (!UUID_RE.test(String(v2TaskId))) return;   // reject composite ids
        if (!markAsComplete) return;                    // not a genuine save
        // Reset immediately so a subsequent cancel on the same lead doesn't re-fire.
        dispatch(updateCompleteStatus(false));
        dispatch(completeTask(practiceId, v2TaskId, () => {}));
    };

    const handleDialogClose = () => {
        endScrollLock();
        dpatch(setLoader(false));
        dpatch(clearLead());
        dpatch(setCurrentTreatmentDataGlobal(null));
        dpatch(updateSteps(null));
        dpatch(updateFollowUpLead(null));
        onCloseConsultationAttend();
        onCloseRecordAttend();
        onCloseUnRecordAttend();
        onCloseContacts();
        openContactWhatsappRef.current = { open: false };
        completeV2TaskIfGenuineOutcome();
        dispatch(getTaskSummary(practiceId));
        if (onCloseCallback) onCloseCallback();
    };

    const handleContactClose = (closeStatus) => {
        endScrollLock();
        dpatch(clearLead());
        dpatch(setCurrentTreatmentDataGlobal(null));
        dpatch(updateSteps(null));
        onCloseContacts();
        openContactWhatsappRef.current = { open: false };
        const genuineOutcome = String(closeStatus || '').toLowerCase() === 'finish'
            || Boolean(markAsComplete);
        completeV2TaskIfGenuineOutcome();
        if (completionOwnedExternally && onCloseCallback) onCloseCallback({ genuineOutcome });
        dispatch(getTaskSummary(practiceId));
        if (!completionOwnedExternally && onCloseCallback) onCloseCallback();
    };

    const handleArchiveClose = () => {
        onCloseArchiveLead();
        setArchiveLeadData(null);
        dpatch(updateFollowUpLead('contact'));
        dispatch(getTaskSummary(practiceId));
        if (onCloseCallback) onCloseCallback();
    };

    const handleArchiveSubmit = ({ data, openCommunication, notes, archiveData }) => {
        const archiveRow = archiveLeadData || rowLead;
        const leadId = archiveRow?.lead_id || archiveRow?.id;
        if (!leadId || !practiceId) return;
        dispatch(beginArchiveLead({
            leadId,
            practiceId,
            leadTreatmentId: archiveRow?.treatment_id || null,
            reason: data?.archiveReason || notes?.note || '',
            note: data?.archiveNotes || archiveData?.note || notes?.additional_notes || '',
            isOpenForCommunication: openCommunication?.is_open_for_communication,
            source: 'tasks-dashboard:legacy-followup-flow',
            handleClose: handleArchiveClose,
        }));
    };

    // ---- Triggers (mirrors TaskDashboard handlers) ----

    // Opens OpenContactLeadPopup (NEW / follow-up contact)
    const triggerContact = (id, reminder) => {
        localStorageRemoveItem('localTreatmentData');
        if (reminder?.lead_treatment?.status === 'NEW') {
            dpatch(getActivities(practiceId, id, 10, 0));
        }
        dpatch(setCurrentTreatmentDataGlobal(null));
        dpatch(updateFollowUpLead('contact'));
        buildRowLeadFromData(reminder);
        reminderDataId.current = reminder?.id;
        setLead(id);
        dpatch(followUpSlice(id, practiceId));
        endScrollLock();
        scrollTimer.current = setTimeout(() => {
            scrollTimer.current = null;
            onOpenContacts();
            watchdog.current = setTimeout(() => {
                watchdog.current = null;
                if (popupSeen.current) return;
                onCloseContacts();
                endScrollLock();
                enqueueSnackbar('Couldn\u2019t open the follow-up screen. Try again.', { variant: 'warning' });
                if (onCloseCallback) onCloseCallback();
            }, POPUP_WATCHDOG_MS);
        }, 800);
    };

    // Opens OpenConsultationPopup (CONSULTATION_ATTENDED)
    const triggerAttendedPopup = (id, reminder) => {
        localStorageRemoveItem('localTreatmentData');
        dpatch(updateFollowUpLead('consultation'));
        buildRowLeadFromData(reminder);
        reminderDataId.current = reminder?.id;
        setLead(id);
        dpatch(followUpSlice(id, practiceId));
        dpatch(setCurrentTreatmentDataGlobal({
            lead_id: reminder?.lead?.id || id || '',
            first_name: reminder?.lead?.first_name || '',
            last_name: reminder?.lead?.last_name || '',
            treatmentData: reminder?.lead_treatment,
        }));
        setTimeout(() => {
            dpatch(getAppointment(practiceId, id));
            onOpenConsultationAttend(id);
        }, 800);
    };

    // Opens OpenUnAttendedPopup (FAILED_TO_ATTEND_CONSULTATION)
    const triggerAttendFailedClick = (id, reminder) => {
        localStorageRemoveItem('localTreatmentData');
        buildRowLeadFromData(reminder);
        reminderDataId.current = reminder?.id;
        const targetId = (id && reminder) ? id : Lead;
        dpatch(followUpSlice(targetId, practiceId));
        dpatch(updateFollowUpLead('UnAttended'));
        // Dispatch getAppointment at the same time the popup opens (mirrors
        // triggerAttendClick). Previously getAppointment fired at t=300ms while
        // followUpSlice fired at t=0 — if getAppointment resolved first, its
        // lead_appointment data was erased when followUpSlice later dispatched
        // followUpLead() which rebuilds followLead from scratch (overwriting the
        // lead_appointment the getAppointment call had just set). Moving both to
        // t=800ms ensures followUpSlice has resolved and followLead is populated
        // before getAppointment fires, preserving lead_appointment correctly.
        setTimeout(() => {
            dpatch(getAppointment(practiceId, targetId));
            onOpenUnRecordAttend();
        }, 800);
    };

    // Opens OpenAttendedPopup (CONSULTATION_BOOKED → record attended)
    const triggerAttendClick = (leadLeadId, reminder) => {
        buildRowLeadFromData(reminder);
        reminderDataId.current = reminder?.id;
        dpatch(updateFollowUpLead('Attended'));
        dpatch(followUpSlice((leadLeadId || Lead), practiceId));
        dpatch(setCurrentTreatmentDataGlobal({
            lead_id: reminder?.lead?.id || '',
            first_name: reminder?.lead?.first_name || '',
            last_name: reminder?.lead?.last_name || '',
            treatmentData: reminder?.lead_treatment,
        }));
        setTimeout(() => {
            dpatch(getAppointment(practiceId, (leadLeadId || Lead)));
            onOpenRecordAttend(leadLeadId || Lead);
        }, 800);
    };

    // Main router (mirrors handleLegacyFollowUp) — dispatches to the right trigger
    const triggerFollowUp = (status, id, reminder, leadTreatment, archive) => {
        if (archive) return;
        if (!leadTreatment) return;
        if (status === 'CLOSED' || status === 'TREATMENT_STARTED') {
            if (status === 'CLOSED') {
                navigate(`/dashboard/practice/${practiceId}/lead-detail/${id}`);
            }
            return;
        }
        if (status === 'CONSULTATION_ATTENDED') {
            triggerAttendedPopup(id, reminder);
            return;
        }
        if (status === 'FAILED_TO_ATTEND_CONSULTATION') {
            triggerAttendFailedClick(id, reminder);
            return;
        }
        triggerContact(id, reminder, status);
    };

    // Build a reminder-shaped object from V2 leadData (TaskChecklistField shape).
    //
    // The V2 cell receives `leadData = params.row` from useLeadTableHelper, which has:
    //   - leadData.originalData = the raw lead API item with `lead_treatments[]` (plural array)
    //   - leadData.treatmentData = the raw `lead_treatments[]` array (alias)
    //   - flat fields: id, lead_id, treatment_id, first_name, last_name, status,
    //     treatment_name, price, lead_contacted_counts (array), clinCheckData,
    //     leadTreatmentSpecification, appointments, created_at_human, etc.
    //
    // The previous implementation tried `leadData.originalData.lead_treatment` (singular),
    // which does not exist — that left lead_treatment as a near-empty object with several
    // nulls, so `buildRowLeadFromData` produced a rowLead that the popups rendered as blank.
    //
    // Order of preference:
    //   1. originalData.lead_treatments[0] / treatmentData[0] / lead_treatments[0] — full API shape
    //   2. originalData.lead_treatment (singular — covers any legacy V1-like reminder)
    //   3. Reconstruct from flat fields (last-resort, still includes all required keys)
    const buildReminderFromLeadData = (leadData) => {
        const leadId = leadData?.id || leadData?.lead_id;
        // Pick the freshest treatment from any of the possible array sources
        const treatmentsArr =
            leadData?.originalData?.lead_treatments ||
            leadData?.treatmentData ||
            leadData?.lead_treatments ||
            [];
        const matchedTreatment =
            (Array.isArray(treatmentsArr) &&
                (treatmentsArr.find((t) => t?.id && t.id === leadData?.treatment_id) ||
                    treatmentsArr.find((t) => t?.status === leadData?.status) ||
                    treatmentsArr[0])) ||
            null;

        const lead = leadData?.originalData?.lead ||
            leadData?.originalData || {
                id: leadId,
                first_name: leadData?.first_name,
                last_name: leadData?.last_name,
                created_at_human: leadData?.created_at_human,
                lead_contact: leadData?.lead_contact || {
                    email: leadData?.email,
                    phone: leadData?.phone,
                },
            };

        const lead_treatment =
            matchedTreatment ||
            leadData?.originalData?.lead_treatment ||
            leadData?.lead_treatment || {
                id: leadData?.treatment_id,
                lead: leadId,
                status: leadData?.status,
                treatment: leadData?.treatment_name,
                price: leadData?.price,
                lead_contacted_counts: Array.isArray(leadData?.lead_contacted_counts)
                    ? leadData.lead_contacted_counts
                    : [],
                leadtreatment_clincheck: leadData?.clinCheckData,
                leadtreatment_specification: leadData?.leadTreatmentSpecification,
                appointments: leadData?.appointments || [],
                is_treatment_fee_pending: leadData?.is_treatment_fee_pending,
            };

        return {
            id: leadData?.id,
            lead,
            lead_treatment,
        };
    };

    // ---- Dialog nodes — must be rendered by the consumer ----
    const dialogs = (
        <>
            <OpenAttendedPopup
                open={openRecordAttend}
                isOverview
                headerButtons
                onClose={handleDialogClose}
                user={rowLead}
                type="recordAttended"
                CreateLeadHeaderData={CreateLeadHeaderData}
            />
            {openContacts && (
                <ContactLeadPopupRouter
                    open={openContacts}
                    // rowLead carries no practice, so without this the router never learns whether the
                    // practice uses Follow Up V2, stays "loading" and renders nothing: a frozen page.
                    practiceId={practiceId}
                    onReady={onContactReady}
                    headerButtons
                    debugSource={`tasks-dashboard:legacy-followup-flow→Contact(status=${rowLead?.status || 'NEW'})`}
                    onClose={handleContactClose}
                    openArchivePopup={() => {
                        setArchiveLeadData(rowLead);
                        onOpenArchiveLead();
                    }}
                    user={rowLead}
                    type="followup"
                    CreateLeadHeaderData={CreateLeadHeaderData}
                />
            )}
            <OpenUnAttendedPopup
                open={openUnRecordAttend}
                headerButtons
                onClose={handleDialogClose}
                user={rowLead}
                type="recordUnAttended"
                CreateLeadHeaderData={CreateLeadHeaderData}
            />
            {openArchiveLead && (
                <ArchivedLeadPopUp
                    open={openArchiveLead}
                    headerButtons
                    onClose={handleArchiveClose}
                    user={archiveLeadData || rowLead}
                    type="archiveLead"
                    CreateLeadHeaderData={CreateLeadHeaderData}
                    onExplicitArchiveSubmit={handleArchiveSubmit}
                />
            )}
            <OpenConsultationPopup
                open={openConsultationAttend}
                headerButtons
                onClose={handleDialogClose}
                user={rowLead}
                type="consultationAttended"
                CreateLeadHeaderData={CreateLeadHeaderData}
            />
        </>
    );

    return {
        triggerFollowUp,
        triggerContact,
        triggerAttendedPopup,
        triggerAttendClick,
        triggerAttendFailedClick,
        buildReminderFromLeadData,
        dialogs,
    };
}
